import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { VerdictBadge, ConfidenceTag, ScoreBar, ProvenanceTag, Panel } from "@/components/common";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { ArrowLeft, ChevronDown, RefreshCw } from "lucide-react";

const SRC_COLORS = { trends: "#60a5fa", amazon: "#fbbf24", reddit: "#f472b6", tiktok: "#34d399" };
const SRC_METRIC = { trends: "search_interest_us", amazon: "review_count", reddit: "mention_count", tiktok: "view_sum" };

function agoLabel(hours) {
  if (hours == null) return "—";
  if (hours < 1) return "Updated <1h ago";
  if (hours < 48) return `Updated ${Math.round(hours)}h ago`;
  return `Updated ${Math.round(hours / 24)}d ago`;
}

function buildChart(timeline) {
  const byTime = {};
  Object.entries(timeline).forEach(([src, rows]) => {
    const metric = SRC_METRIC[src];
    const vals = rows.map((r) => r.metrics[metric]).filter((v) => v != null);
    const max = Math.max(...vals, 1);
    rows.forEach((r) => {
      const t = r.observed_at.slice(0, 10);
      byTime[t] = byTime[t] || { t };
      const v = r.metrics[metric];
      if (v != null) byTime[t][src] = Math.round((v / max) * 100);
    });
  });
  return Object.values(byTime).sort((a, b) => a.t.localeCompare(b.t));
}

function EvidenceSource({ src, rows }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#27272A]">
      <button
        data-testid={`evidence-source-${src}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#141416] transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="w-2 h-2" style={{ background: SRC_COLORS[src] }} />
          <span className="font-mono text-sm uppercase tracking-widest">{src}</span>
          <span className="text-[10px] text-[#71717A] font-mono">{rows.length} observations</span>
          <ProvenanceTag type="Observed" />
        </div>
        <ChevronDown size={16} className={`text-[#71717A] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[#27272A] overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-[#71717A] border-b border-[#27272A]">
                <th className="text-left px-4 py-2">observed_at</th>
                {Object.keys(rows[rows.length - 1].metrics).map((k) => (
                  <th key={k} className="text-right px-3 py-2">{k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-[#1c1c1f]">
                  <td className="px-4 py-1.5 text-[#A1A1AA]">{r.observed_at.slice(0, 10)}</td>
                  {Object.keys(rows[rows.length - 1].metrics).map((k) => (
                    <td key={k} className="text-right px-3 py-1.5 text-[#F4F4F5]">
                      {r.metrics[k] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ScoreInputs({ name, sc }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#27272A]">
      <button
        data-testid={`score-inputs-${name}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#141416] transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm uppercase tracking-widest">{name}</span>
          <span className="font-mono text-[#F4F4F5]">= {String(sc.value)}</span>
          <ProvenanceTag type="Calculated" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#71717A] font-mono">{sc.formula_version} · cfg v{sc.config_version}</span>
          <ChevronDown size={16} className={`text-[#71717A] transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <pre className="border-t border-[#27272A] p-4 text-[11px] font-mono text-[#A1A1AA] overflow-x-auto">
          {JSON.stringify(sc.inputs, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [ev, setEv] = useState(null);
  const [narr, setNarr] = useState(null);

  const load = () => {
    api.get(`/products/${id}`).then((r) => setD(r.data));
    api.get(`/products/${id}/evidence`).then((r) => setEv(r.data));
    api.get(`/products/${id}/narrative`).then((r) => setNarr(r.data));
  };
  useEffect(load, [id]);

  if (!d) return <div className="p-8 text-[#71717A] font-mono text-sm">Loading…</div>;
  const p = d.product;
  const v = d.verdict || {};
  const s = d.scores || {};
  const chart = buildChart(d.timeline || {});

  return (
    <div className="p-8 max-w-[1400px]">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-xs text-[#A1A1AA] hover:text-[#F4F4F5] mb-4 font-mono">
        <ArrowLeft size={14} /> back
      </button>

      {/* Header */}
      <div className="border border-[#27272A] bg-[#18181B] p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 data-testid="product-name" className="font-head text-2xl font-bold tracking-tight">{p.canonical_name}</h1>
              <VerdictBadge decision={v.decision} />
            </div>
            <div className="text-xs text-[#71717A] font-mono mt-1">
              {p.category} · market stage: {p.tier} · first detected {String(p.first_detected_at).slice(0, 10)}
            </div>
            {p.category_flags?.length > 0 && (
              <div className="mt-2 inline-block text-[10px] font-mono text-orange-400 border border-orange-900 bg-orange-950/20 px-2 py-0.5">
                CATEGORY FLAG (metadata only in V1): {p.category_flags.join(", ")}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="font-mono text-4xl font-bold text-[#F4F4F5]">{v.composite ?? "—"}</div>
            <div className="text-[10px] text-[#71717A] tracking-widest">COMPOSITE (RANKING)</div>
            <div className="mt-2"><ConfidenceTag level={v.confidence} /></div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-[#27272A] flex flex-wrap gap-2">
          {(v.reason_codes || []).map((rc) => (
            <span key={rc} data-testid={`reason-${rc}`} className="text-[11px] font-mono border border-[#3f3f46] bg-[#09090B] px-2 py-1 text-[#F4F4F5]">
              {rc}
            </span>
          ))}
          <button
            data-testid="recompute-button"
            onClick={() => api.post(`/products/${id}/recompute`).then(load)}
            className="ml-auto flex items-center gap-1 text-[11px] font-mono text-[#A1A1AA] hover:text-emerald-400 border border-[#27272A] px-2 py-1"
          >
            <RefreshCw size={12} /> recompute
          </button>
        </div>
      </div>

      {/* Freshness badges (from actual observation timestamps; feeds confidence_v1) */}
      <div data-testid="freshness-badges" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {(d.freshness || []).map((f) => (
          <div key={f.source} data-testid={`freshness-${f.source}`} className="border border-[#27272A] bg-[#18181B] p-3">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-mono text-[#A1A1AA]">
              <span className="w-2 h-2" style={{ background: SRC_COLORS[f.source] }} /> {f.source}
            </div>
            {f.available ? (
              <div className={`mt-1 font-mono text-sm ${f.stale ? "text-amber-400" : "text-[#F4F4F5]"}`}>
                {agoLabel(f.hours_ago)}
                {f.stale && <span className="text-[10px] ml-1 text-amber-400">STALE</span>}
              </div>
            ) : (
              <div className="mt-1 font-mono text-sm text-[#71717A]">Unavailable</div>
            )}
          </div>
        ))}
      </div>

      {/* Decision / Rejected explainer */}
      {(d.explanations || []).length > 0 && (
        <div data-testid="decision-explainer" className="mb-6">
          <Panel title={v.decision === "REJECT" ? "Why we rejected it" : "Why this decision"}>
            <div className="space-y-4">
              {d.explanations.map((e) => (
                <div key={e.reason_code} data-testid={`explainer-${e.reason_code}`} className="border border-[#27272A] bg-[#09090B] p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <span className="font-mono text-[11px] border border-[#3f3f46] px-2 py-1 text-[#F4F4F5]">{e.reason_code}</span>
                    <span className="text-[10px] font-mono text-[#71717A]">
                      {e.primary_score} = {String(e.score_value)} · {e.formula_version} · cfg v{e.config_version} · confidence {e.confidence}
                    </span>
                  </div>
                  <p className="text-sm text-[#D4D4D8] leading-relaxed mb-3">{e.plain_language}</p>
                  {e.evidence?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {e.evidence.slice(0, 8).map((ev, i) => (
                        <span key={i} className="text-[10px] font-mono border border-blue-900 bg-blue-950/40 text-blue-300 px-2 py-0.5">
                          {ev.label}: {String(ev.value)}
                        </span>
                      ))}
                    </div>
                  )}
                  {Object.keys(e.source_dates || {}).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {Object.entries(e.source_dates).map(([k, val]) => (
                        <span key={k} className="text-[10px] font-mono text-zinc-400 border border-zinc-700 px-2 py-0.5">
                          {k}: {typeof val === "string" ? val.slice(0, 10) : String(val)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* Why found — deterministic unless the AI layer is explicitly enabled */}
      <div className="mb-6">
        <Panel title="Why Breakout Radar flagged it" right={<ProvenanceTag type={narr?.provenance === "AI Interpretation" ? "AI Interpretation" : "Calculated"} />}>
          <p data-testid="narrative-text" className="text-sm text-[#D4D4D8] leading-relaxed">
            {narr ? narr.text : "…"}
          </p>
          {narr && narr.source === "deterministic" && (
            <p data-testid="narrative-source-note" className="mt-2 text-[10px] font-mono text-[#71717A]">Calculated from stored evidence — AI layer frozen, no AI call made.</p>
          )}
        </Panel>
      </div>

      {/* Scores */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <ScoreBar label="Newness" value={s.newness?.value} testid="score-newness" />
        <ScoreBar label="Breakout" value={s.breakout?.value} testid="score-breakout" />
        <ScoreBar label="Commerce" value={s.commerce?.value} testid="score-commerce" />
        <div data-testid="score-trendquality" className="border border-[#27272A] bg-[#18181B] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-mono mb-2">Trend Quality</div>
          <div className={`font-mono text-sm ${s.trendquality?.value === "SUSTAINED_ACCELERATION" ? "text-emerald-400" : s.trendquality?.value === "VIRAL_NOISE" ? "text-rose-400" : "text-amber-400"}`}>
            {s.trendquality?.value || "—"}
          </div>
        </div>
        <div data-testid="score-confidence" className="border border-[#27272A] bg-[#18181B] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-mono mb-2">Data Confidence</div>
          <div className="font-mono text-sm text-[#F4F4F5]">{s.confidence?.value || "—"}</div>
        </div>
      </div>

      {/* Timeline */}
      <div className="mb-6">
        <Panel title="Signal Timeline (normalized 0–100 per source)" right={<ProvenanceTag type="Observed" />}>
          {chart.length === 0 ? (
            <div className="text-xs text-[#71717A] font-mono">No time-series data.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chart}>
                <CartesianGrid stroke="#27272A" vertical={false} />
                <XAxis dataKey="t" stroke="#71717A" fontSize={10} tickLine={false} />
                <YAxis stroke="#71717A" fontSize={10} tickLine={false} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #27272A", fontFamily: "JetBrains Mono", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 11 }} />
                {Object.keys(d.timeline || {}).map((src) => (
                  <Line key={src} type="monotone" dataKey={src} stroke={SRC_COLORS[src]} dot={false} strokeWidth={2} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      {/* Evidence Explorer */}
      <div data-testid="evidence-explorer">
        <h2 className="font-head font-semibold tracking-tight mb-3">Evidence Explorer</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Calculated scores → inputs</div>
            <div className="space-y-2">
              {ev &&
                Object.entries(ev.scores).map(([name, sc]) => <ScoreInputs key={name} name={name} sc={sc} />)}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Observed evidence by source</div>
            <div className="space-y-2">
              {ev &&
                Object.entries(ev.observed_by_source).map(([src, rows]) => (
                  <EvidenceSource key={src} src={src} rows={rows} />
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
